import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface DateRangeSelection {
  startDate: Date | null;
  endDate: Date | null;
  presetId?: string;
  label: string;
}

export interface PresetOption {
  id: string;
  label: string;
  getRange: () => { startDate: Date; endDate: Date };
}

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './date-range-picker.component.html',
})
export class DateRangePickerComponent implements OnInit, OnChanges {
  @Input() startDate: Date | null = null;
  @Input() endDate: Date | null = null;
  @Input() activePresetId: string | null = '30d';
  @Input() align: 'left' | 'right' = 'left';
  @Input() loading: boolean = false;

  @Output() rangeChange = new EventEmitter<DateRangeSelection>();

  isOpen = signal<boolean>(false);

  // Calendar state
  viewDate = signal<Date>(new Date());
  
  // Selection state
  tempStartDate = signal<Date | null>(null);
  tempEndDate = signal<Date | null>(null);
  hoverDate = signal<Date | null>(null);
  selectedPreset = signal<string | null>(null);

  private elementRef = inject(ElementRef);

  presets: PresetOption[] = [
    {
      id: '7d',
      label: 'Last 7 days',
      getRange: () => {
        const now = new Date();
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        return { startDate: start, endDate: end };
      },
    },
    {
      id: '30d',
      label: 'Last 30 days',
      getRange: () => {
        const now = new Date();
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const start = new Date(end);
        start.setDate(start.getDate() - 29);
        return { startDate: start, endDate: end };
      },
    },
    {
      id: 'this_month',
      label: 'This month',
      getRange: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { startDate: start, endDate: end };
      },
    },
  ];

  ngOnInit(): void {
    this.syncFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const rangeInputs = ['activePresetId', 'startDate', 'endDate'];
    const rangeChanged = rangeInputs.some((key) => changes[key] && !changes[key].firstChange);
    if (rangeChanged) {
      this.syncFromInputs();
    }
  }

  private syncFromInputs(): void {
    if (this.activePresetId) {
      this.selectPresetById(this.activePresetId, false);
    } else if (this.startDate && this.endDate) {
      this.selectedPreset.set(null);
      this.tempStartDate.set(this.startDate);
      this.tempEndDate.set(this.endDate);
      this.viewDate.set(new Date(this.endDate));
    } else {
      this.selectPresetById('30d', false);
    }
  }

  // Label shown on top trigger button
  displayLabel = computed(() => {
    const presetId = this.selectedPreset();
    if (presetId) {
      const preset = this.presets.find((p) => p.id === presetId);
      if (preset) return preset.label;
    }
    const start = this.tempStartDate();
    const end = this.tempEndDate();
    if (start && end) {
      if (start.getTime() === end.getTime()) {
        return this.formatShortDate(start);
      }
      return `${this.formatShortDate(start)} - ${this.formatShortDate(end)}`;
    } else if (start) {
      return `${this.formatShortDate(start)} - ...`;
    }
    return 'Select date range';
  });

  // Calendar month/year header string
  monthYearLabel = computed(() => {
    const d = this.viewDate();
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  });

  isNextMonthDisabled = computed(() => {
    const view = this.viewDate();
    const today = new Date();
    return (
      view.getFullYear() > today.getFullYear() ||
      (view.getFullYear() === today.getFullYear() && view.getMonth() >= today.getMonth())
    );
  });

  // Days grid generation for current viewDate
  calendarDays = computed(() => {
    const date = this.viewDate();
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const startDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days: Array<{
      date: Date;
      dayNumber: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isFuture: boolean;
    }> = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < startDayOfWeek; i++) {
      const prevDate = new Date(year, month, 1 - (startDayOfWeek - i));
      days.push({
        date: prevDate,
        dayNumber: prevDate.getDate(),
        isCurrentMonth: false,
        isToday: false,
        isFuture: prevDate.getTime() > today.getTime(),
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const current = new Date(year, month, day);
      current.setHours(0, 0, 0, 0);
      days.push({
        date: current,
        dayNumber: day,
        isCurrentMonth: true,
        isToday: current.getTime() === today.getTime(),
        isFuture: current.getTime() > today.getTime(),
      });
    }

    return days;
  });

  toggleDropdown(): void {
    this.isOpen.update((v) => !v);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  prevMonth(event: MouseEvent): void {
    event.stopPropagation();
    const current = this.viewDate();
    this.viewDate.set(new Date(current.getFullYear(), current.getMonth() - 1, 1));
  }

  nextMonth(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isNextMonthDisabled()) return;
    const current = this.viewDate();
    this.viewDate.set(new Date(current.getFullYear(), current.getMonth() + 1, 1));
  }

  selectPreset(preset: PresetOption, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    const range = preset.getRange();
    this.tempStartDate.set(range.startDate);
    this.tempEndDate.set(range.endDate);
    this.selectedPreset.set(preset.id);
    this.viewDate.set(new Date(range.endDate));
  }

  selectPresetById(id: string, emit = true): void {
    const preset = this.presets.find((p) => p.id === id);
    if (preset) {
      const range = preset.getRange();
      this.tempStartDate.set(range.startDate);
      this.tempEndDate.set(range.endDate);
      this.selectedPreset.set(preset.id);
      this.viewDate.set(new Date(range.endDate));
      if (emit) {
        this.applyRange();
      }
    }
  }

  onDayClick(dayObj: { date: Date; isCurrentMonth: boolean; isFuture?: boolean }, event: MouseEvent): void {
    event.stopPropagation();
    if (!dayObj.isCurrentMonth || dayObj.isFuture) return;

    const clickedDate = new Date(dayObj.date);
    clickedDate.setHours(0, 0, 0, 0);

    const start = this.tempStartDate();
    const end = this.tempEndDate();

    if (!start || (start && end)) {
      this.tempStartDate.set(clickedDate);
      this.tempEndDate.set(null);
      this.selectedPreset.set(null);
    } else {
      if (clickedDate.getTime() >= start.getTime()) {
        this.tempEndDate.set(clickedDate);
        this.selectedPreset.set(null);
      } else {
        this.tempStartDate.set(clickedDate);
        this.tempEndDate.set(null);
        this.selectedPreset.set(null);
      }
    }
  }

  onDayMouseEnter(dayObj: { date: Date; isCurrentMonth: boolean; isFuture?: boolean }): void {
    if (dayObj.isCurrentMonth && !dayObj.isFuture && this.tempStartDate() && !this.tempEndDate()) {
      this.hoverDate.set(dayObj.date);
    }
  }

  onDayMouseLeave(): void {
    this.hoverDate.set(null);
  }

  isDayInRange(date: Date): boolean {
    const d = date.getTime();
    const start = this.tempStartDate()?.getTime();
    const end = this.tempEndDate()?.getTime();
    const hover = this.hoverDate()?.getTime();

    if (start && end) {
      return d >= start && d <= end;
    }
    if (start && hover && !end) {
      if (hover >= start) {
        return d >= start && d <= hover;
      }
    }
    return false;
  }

  isDayRangeStart(date: Date): boolean {
    const d = date.getTime();
    const start = this.tempStartDate()?.getTime();
    return d === start;
  }

  isDayRangeEnd(date: Date): boolean {
    const d = date.getTime();
    const start = this.tempStartDate()?.getTime();
    const end = this.tempEndDate()?.getTime();
    const hover = this.hoverDate()?.getTime();

    if (end) return d === end;
    if (hover && start && hover >= start) {
      return d === hover;
    }
    if (start && !end && (!hover || hover < start)) {
      return d === start;
    }
    return false;
  }

  getDayClass(dayObj: { date: Date; dayNumber: number; isCurrentMonth: boolean; isToday: boolean; isFuture: boolean }): string {
    const base = 'w-full h-full flex items-center justify-center text-xs select-none transition-all cursor-pointer ';

    if (dayObj.isFuture) {
      return base + 'text-text-dimmed/30 opacity-30 cursor-not-allowed pointer-events-none';
    }

    const inRange = this.isDayInRange(dayObj.date);
    const isStart = this.isDayRangeStart(dayObj.date);
    const isEnd = this.isDayRangeEnd(dayObj.date);

    if (inRange) {
      if (isStart && isEnd) {
        return base + 'bg-button-primary-default text-text-inverted-primary font-bold rounded-lg shadow-sm';
      }
      if (isStart) {
        return base + 'bg-button-primary-default text-text-inverted-primary font-bold rounded-l-lg';
      }
      if (isEnd) {
        return base + 'bg-button-primary-default text-text-inverted-primary font-bold rounded-r-lg';
      }
      if (dayObj.isToday) {
        return base + 'bg-white/15 text-text-primary font-bold ring-1 ring-white/30 rounded-none';
      }
      return base + 'bg-white/10 text-text-primary font-medium rounded-none';
    }

    if (dayObj.isToday) {
      return base + 'text-text-primary font-bold border border-card-border-border bg-white/5 hover:bg-combobox-list-item-hover-bg rounded-lg';
    }

    return base + 'text-text-primary hover:bg-combobox-list-item-hover-bg rounded-lg font-normal';
  }

  applyRange(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    const start = this.tempStartDate();
    const end = this.tempEndDate() || start;
    const presetId = this.selectedPreset() || undefined;

    this.rangeChange.emit({
      startDate: start,
      endDate: end,
      presetId,
      label: this.displayLabel(),
    });
    this.closeDropdown();
  }

  private formatShortDate(d: Date): string {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }
}
